import SEO from '@/components/seo';
import Track404 from '@/components/notFoundPages/trackNotFound';
import React, { useMemo } from 'react';
import { isRouteErrorResponse, useLocation, useRouteError } from 'react-router-dom';
import { cap } from '@/components/_helpers';
import { toArtistSlug } from '@/utils/artistSlug';

const OLD_BLOG_PATTERN = /^\/\d{4}\/\d{1,2}\//;

const HOME_ARTIST = 'Truevined';

const HOME_ARTIST_USERNAME = 'truevined';

const NotFoundPage: React.FC = () => {
  const error = useRouteError();
  const location = useLocation();

  const { artist, artistUsername, attemptedPath, errorType } = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    if (OLD_BLOG_PATTERN.test(location.pathname)) {
      return {
        artist: HOME_ARTIST,
        artistUsername: HOME_ARTIST_USERNAME,
        attemptedPath: pathSegments[pathSegments.length - 1]?.replace('.html', '') || 'blog-post',
        errorType: 'blog' as const,
      };
    }

    // Only a release path carries a real artist segment. Anything else is an
    // unknown root such as /help, and treating that segment as an artist gave
    // the 404 page a nav for an artist that does not exist, with dead links.
    const isRelease = pathSegments.length > 1 && (pathSegments.includes('songs') || pathSegments.includes('albums'));

    if (!isRelease) {
      return {
        artist: HOME_ARTIST,
        artistUsername: HOME_ARTIST_USERNAME,
        attemptedPath: location.pathname,
        errorType: 'page' as const,
      };
    }

    const releaseArtist = pathSegments[0].replace(/-/g, ' ');

    return {
      artist: cap(releaseArtist),
      artistUsername: toArtistSlug(releaseArtist),
      attemptedPath: pathSegments[pathSegments.length - 1],
      errorType: pathSegments.includes('songs') ? ('track' as const) : ('album' as const),
    };
  }, [location.pathname]);

  let errorMessage = '404 - Page Not Found';

  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText || errorMessage;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }

  const getMessage = () => {
    switch (errorType) {
      case 'track':
        return `doesn't have a track called "${cap(attemptedPath.replace(/-/g, ' '))}" yet. Check out their other music!`;
      case 'album':
        return `doesn't have an album called "${cap(attemptedPath.replace(/-/g, ' '))}" yet. Browse their discography!`;
      case 'blog':
        return `had a blog here, but it has been discontinued. Listen on Spotify instead!`;
      default:
        return "doesn't have this page. Let's get you back to the music!";
    }
  };

  const getTitle = () => {
    switch (errorType) {
      case 'track':
        return 'Track Not Found';
      case 'album':
        return 'Album Not Found';
      case 'blog':
        return 'Blog Removed';
      default:
        return 'Page Not Found';
    }
  };

  const song = {
    artist,
    artistUsername,
    title: attemptedPath,
  };

  return (
    <>
      <SEO
        title={`${getTitle()} | ${artist}`}
        description={`The ${errorType} you're looking for doesn't exist. Explore ${artist}'s music instead!`}
        type="website"
      />
      <Track404
        song={song}
        pageTitle={getTitle()}
        message={getMessage()}
        errorType={errorType}
      />
    </>
  );
};

export default NotFoundPage;
